import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import type { Job, ModuleRun } from '@/types/forensics';

const TERMINAL_STATUSES = new Set(['complete', 'partial_complete', 'failed', 'canceled']);

export function useJobPolling(jobId: string | undefined, intervalMs = 3000) {
  const [job, setJob] = useState<Job | null>(null);
  const [moduleRuns, setModuleRuns] = useState<ModuleRun[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await apiFetch(`/api/jobs/${jobId}`);
      if (!response.ok) return;
      const data = await response.json();
      setJob(data);
      setModuleRuns(data.moduleRuns || []);

      if (TERMINAL_STATUSES.has(data.status)) {
        setIsPolling(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch {
      // Silently continue polling on error
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;

    // Initial fetch
    setIsPolling(true);
    fetchJob();

    intervalRef.current = setInterval(fetchJob, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [jobId, intervalMs, fetchJob]);

  return { job, moduleRuns, isPolling };
}
